import type { OnboardingRequestStatus } from '@console/types'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  acceptNamedOnboardingRequest,
  confirmMergeOtp,
  fetchOnboardingRequests,
  rejectOnboardingRequest,
  requestMergeOtp,
} from './api.js'

const QUERY_KEY = (status: string) => ['onboarding-requests', status] as const

export function useOnboardingRequests(
  status: Extract<OnboardingRequestStatus, 'pending' | 'rejected'>,
) {
  return useQuery({
    queryKey: QUERY_KEY(status),
    queryFn: () => fetchOnboardingRequests(status),
  })
}

function invalidateBoth(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: QUERY_KEY('pending') })
  void queryClient.invalidateQueries({ queryKey: QUERY_KEY('rejected') })
}

export function useAcceptNamedOnboardingRequest() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, tenantName }: { id: string; tenantName: string }) =>
      acceptNamedOnboardingRequest(id, tenantName),
    onSuccess: () => {
      invalidateBoth(queryClient)
    },
  })
}

export function useRequestMergeOtp() {
  return useMutation({
    mutationFn: ({ id, tenantId }: { id: string; tenantId: string }) =>
      requestMergeOtp(id, tenantId),
  })
}

export function useConfirmMergeOtp() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, tenantId, otp }: { id: string; tenantId: string; otp: string }) =>
      confirmMergeOtp(id, tenantId, otp),
    onSuccess: () => {
      invalidateBoth(queryClient)
    },
  })
}

export function useRejectOnboardingRequest() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: rejectOnboardingRequest,
    onSuccess: () => {
      invalidateBoth(queryClient)
    },
  })
}
